import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject, formatDate } from "./utils/utils";

export default {
  name: "IdpCourseCompletionMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 12,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "structured_learning",
        type: "completion",
        idp_period_id: null,
        all: false,
        coaching_type_id: null,
      },
      isLoading: false,
      isModalLoading: false,
      isLoadingSummary: false,
      isUpdateSummaryLoading: false,
      isUpdateActivityLoading: false,
      isAddScheduleLoading: false,
      isAddProgressLoading: false,
      _searchTimer: null,
      showModal: false,
      modalData: null,
      summaryData: null,
      activityData: null,
      idpPeriodOptions: [],
      activeFiltersCount: 0,
      activeTab: "structured_learning",
    };
  },

  computed: {
    ...mapGetters("courseCompletion", [
      "idpLearnings",
      "summaryIdpLearning",
      "trainingMethods",
      "trainingTypes",
      "competencies",
    ]),

    showingFrom() {
      if (!this.idpLearnings.total) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearnings.total ? this.idpLearnings.total : to;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filters.idp_period_id"() {
      this.filters.page = 1;

      this.loadSummaryLearning(this.filters.learning_type);
      this.loadData();
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("courseCompletion", [
      "getIdpLearnings",
      "getStructuredLearningDetail",
      "getSocialLearningDetail",
      "getExperientialLearningDetail",
      "uploadCertificateLearning",
      "uploadEvidenceLearning",
      "addProgressProjectExperientialLearning",
      "kickOffProjectExperientialLearning",
      "uploadPresentationLearning",
      "updateCoachingSummary",
      "addCoachingSchedules",
      "getSummaryIdpLearning",
      "getIdpPeriod",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "resetIdpLearnings",
      "getSummaryIdpSocialLearning",
    ]),

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    async showUploadCertificateModal(course) {
      try {
        const response = await this.getStructuredLearningDetail({ id: course.id });
        this.modalData = response?.data?.data || response?.data || course;
      } catch (error) {
        this.modalData = course;
      }

      this.$bvModal.show("upload-certificate-modal");
    },

    async showUploadPresentationModal(course) {
      this.modalData = course;

      this.$bvModal.show("upload-presentation-modal");
    },

    async showUpdateSummaryModal(data) {
      this.summaryData = data;
      this.$bvModal.show("update-summary-modal");
    },

    async showUpdateActivityModal(data) {
      this.activityData = data;

      this.$bvModal.show("update-activity-modal");
    },

    async showAddProgressModal() {
      this.$bvModal.show("progress-activity-modal");
    },

    async showScheduleModal() {
      this.$bvModal.show("schedule-modal");
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        type: this.filters.type,
        idp_period_id: this.filters.idp_period_id,
        training_scope: this.filters.training_scope,
        training_category: this.filters.training_category,
        platform: this.filters.platform,
        training_method_id: this.filters.training_method_id,
        training_type: this.filters.training_type,
        competency_id: this.filters.competency_id,
        coaching_type_id: this.filters.coaching_type_id,
      };

      return cleanObject(raw);
    },

    async loadData() {
      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearnings(query);

        if (this.idpLearnings.page) this.filters.page = this.idpLearnings.page;
        if (this.idpLearnings.limit) this.filters.pageSize = this.idpLearnings.limit;
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onTabChange(tab) {
      this.activeTab = tab;

      this.filters.learning_type = tab;
      this.modalData = null;
      this.filters.page = 1;
      this.loadData();
      this.loadSummaryLearning(tab);
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    async openDetailModal(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      switch (this.filters.learning_type) {
        case "structured_learning":
          {
            const detail = await this.getStructuredLearningDetail({ id: row.id });
            this.modalData = detail;
          }
          break;
        case "social_learning":
          {
            const detail = await this.getSocialLearningDetail({ id: row.id });
            this.modalData = detail;
          }
          break;
        case "experiential_learning":
          {
            const detail = await this.getExperientialLearningDetail({ id: row.id });
            this.modalData = detail;
          }
          break;
        default:
          this.modalData = null;
      }

      this.$nextTick(() => {
        if (this.filters.learning_type === "structured_learning") this.$bvModal.show("structured-learning-modal");

        if (this.filters.learning_type === "social_learning") this.$bvModal.show("social-learning-modal");

        if (this.filters.learning_type === "experiential_learning") this.$bvModal.show("experiential-learning-modal");
      });
      this.isModalLoading = false;
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 12,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    async uploadCertificate(payload) {
      try {
        const formData = new FormData();
        formData.append("certificate", payload.certificate);

        await this.uploadCertificateLearning({
          id: payload.id,
          data: formData,
        });

        toast({ type: "success", title: "Success", message: "Sertifikat berhasil diunggah" });

        this.$bvModal.hide("upload-certificate-modal");
        this.$bvModal.hide("structured-learning-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async uploadPresentation(payload) {
      try {
        const formData = new FormData();
        formData.append("presentation", payload.presentation);

        await this.uploadPresentationLearning({
          id: payload.id,
          data: formData,
        });

        toast({ type: "success", title: "Success", message: "Presentasi berhasil diunggah" });

        this.$bvModal.hide("upload-presentation-modal");
        this.$bvModal.hide("social-learning-modal");
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async updateSummary(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin memperbarui ringkasan?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, perbarui",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isUpdateSummaryLoading = true;

            const formData = new FormData();
            formData.append("summary", payload.summary);
            formData.append("follow_up", payload.follow_up);
            if (payload.attachments) formData.append("attachments", payload.attachments);

            await this.updateCoachingSummary({ id: payload.id, data: formData });

            toast({ type: "success", title: "Success", message: "Ringkasan berhasil diperbarui" });
            this.$bvModal.hide("update-summary-modal");
            this.$bvModal.hide("social-learning-modal");
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isUpdateSummaryLoading = false;
            this.loadData();
          }
        }
      });
    },

    async updateActivity(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin memperbarui aktivitas?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, perbarui",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isUpdateActivityLoading = true;

            if (payload?.actual_start_date) {
              await this.kickOffProjectExperientialLearning({
                id: payload.id,
                data: { actual_start_date: payload.actual_start_date },
              });
            }

            if (payload?.evidence) {
              const formData = new FormData();
              formData.append("evidence", payload.evidence);
              await this.uploadEvidenceLearning({ id: payload.id, data: formData });
            }

            toast({ type: "success", title: "Success", message: "Aktivitas berhasil diperbarui" });
            this.$bvModal.hide("update-activity-modal");
            this.$bvModal.hide("experiential-learning-modal");
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isUpdateActivityLoading = false;
            this.loadData();
          }
        }
      });
    },

    async submitProgress(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim progress?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isAddProgressLoading = true;

            const formData = new FormData();
            formData.append("attachments", payload.attachments);
            formData.append("name", payload.name);
            formData.append("date", payload.date);
            formData.append("progress", payload.progress);

            await this.addProgressProjectExperientialLearning({ id: payload.id, data: formData });

            toast({ type: "success", title: "Success", message: "Progress berhasil dikirim" });

            this.$bvModal.hide("progress-activity-modal");
            this.$bvModal.hide("update-activity-modal");
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isAddProgressLoading = false;
            const detail = await this.getExperientialLearningDetail({ id: this.modalData.id });
            this.modalData = detail;
          }
        }
      });
    },

    async submitSchedule(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin menambah jadwal?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isAddScheduleLoading = true;

            await this.addCoachingSchedules({ id: payload.id, data: { schedules: payload.schedules } });

            toast({ type: "success", title: "Success", message: "Jadwal berhasil ditambahkan" });
            this.$bvModal.hide("schedule-modal");
            this.$bvModal.hide("social-learning-modal");
          } catch (error) {
            console.error("Update error:", error);
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isAddScheduleLoading = false;
            this.loadData();
          }
        }
      });
    },

    async downloadCertificate(course) {
      console.log("course :>> ", course);
    },

    async submitEnrollment(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim pendaftaran?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.submitIdpLearning({ query: { learning_type: "structured_learning" }, data: { ...payload } });

            toast({ type: "success", title: "Success", message: "Pendaftaran berhasil!" });
            this.$bvModal.hide("structured-learning-modal");
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.loadData();
          }
        }
      });
    },

    async loadSummaryLearning(type) {
      this.isLoadingSummary = true;

      const learningType = type || this.filters.learning_type;
      const payload = {
        learning_type: learningType,
        type: "completion",
      };

      if (this.filters.idp_period_id) payload.idp_period_id = this.filters.idp_period_id;

      switch (learningType) {
        case "structured_learning":
          await this.getSummaryIdpLearning(payload);
          break;
        case "social_learning":
          await this.getSummaryIdpSocialLearning(payload);
          break;
      }

      this.isLoadingSummary = false;
    },

    async loadIdpPeriod() {
      const idpPeriod = await this.getIdpPeriod();
      const now = new Date();

      this.filters.idp_period_id =
        idpPeriod.find((p) => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.id || null;

      this.idpPeriodOptions = idpPeriod.map((p) => ({
        text: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
      }));
    },

    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return "";

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },

    applyFilters(appliedFilters) {
      const cleaned = cleanObject(appliedFilters);

      Object.entries(cleaned).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.$set(this.filters, "page", 1);
      this.updateActiveFilterCount();
      this.loadData();

      this.$bvModal.hide("modal-filter");
    },

    updateActiveFilterCount() {
      const { training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id } = this.filters;
      const vals = [training_scope, training_category, platform, training_method_id, training_type, competency_id, coaching_type_id];

      this.activeFiltersCount = vals.filter((x) => x !== null && x !== undefined && x !== "").length;
    },

    resetFilters() {
      const reset = {
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        competency_id: null,
        coaching_type_id: null,
        page: 1,
      };

      Object.entries(reset).forEach(([key, val]) => {
        this.$set(this.filters, key, val);
      });

      this.activeFiltersCount = 0;

      this.loadData();
    },

    clearSingleFilter(filterKey) {
      if (this.filters.hasOwnProperty(filterKey)) {
        this.$set(this.filters, filterKey, null);
        this.filters.page = 1;
        this.updateActiveFilterCount();
        this.loadData();
      }
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();
    await this.loadIdpPeriod();

    await Promise.all([
      this.loadSummaryLearning(),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
      this.loadData(),
    ]);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    this.resetIdpLearnings();
  },
};
